import { UniverseAPI, UniverseUIConfiguration } from '@tacans/shared-marketplace-layer';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// RESOURCES
import PageMeta from 'src/components/organisms/page-meta';
import { PAGES_LOCALES } from 'src/constants/common.constants';
import S7Landing from 'src/features/common/landing/s7-landing';
import UniverseLanding from 'src/features/common/landing/universe-landing';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { VerificationProcessModal } from 'src/features/common/universe/modals/verification-process-modal';
import { VERIFICATION_PROCESS_MODAL_ID } from 'src/features/common/universe/modals/verification-process-modal/config';
import { UniverseConfigurationsContext } from 'src/features/common/universe/providers/universe-configurations.context';
import { fetchArtsThunk } from 'src/features/white-labels/techcontemporary/explore/ducks/explore-arts/explore-arts.thunk';
import TechContemporaryLanding from 'src/features/white-labels/techcontemporary/landing/universe-landing';
import { VerificationProcessTechModal } from 'src/features/white-labels/techcontemporary/shared/modals/verification-process-modal';
import { useGetUniverseTemplate } from 'src/hooks/use-get-universe-template';
import { useMeta } from 'src/hooks/use-meta';
import { wrapper } from 'src/store';
import { makeSSRApiRequest } from 'src/utils/api.utils';

const NinjaUniverseLanding = dynamic(
    () => import('src/features/white-labels/ninja/landing/universe-landing/universe-landing')
);
const OceanUniverseLanding = dynamic(
    () => import('src/features/white-labels/ocean/landing/universe-landing/universe-landing')
);
const WorldOfArtistsUniverseLanding = dynamic(
    () => import('src/features/white-labels/worldofartists/landing/universe-landing/universe-landing')
);
const SuperLabUniverseLanding = dynamic(() => import('src/features/white-labels/nftsuperlab/landing/universe-landing'));
const SportsMuseumUniverseLanding = dynamic(
    () => import('src/features/white-labels/thesportmuseum/landing/universe-landing')
);
const PhoenxHoldingUniverseLanding = dynamic(
    () => import('src/features/white-labels/phoenxholding/landing/universe-landing')
);

const SyndicateUniverseLanding = dynamic(() => import('src/features/white-labels/syndicate/landing/universe-landing'));

const MarketplacePage: React.VFC<{ uiConfigurations: UniverseUIConfiguration[] }> = ({ uiConfigurations = [] }) => {
    const [configurations, setConfigurations] = useState<UniverseUIConfiguration[]>(uiConfigurations ?? []);
    const { isUniverse, projectName, domain, visible, isUniverseOwner } = useUniverse();
    const {
        isOceanTemplate,
        isNinjaTemplate,
        isNftSuperlab,
        isTechContemporary,
        isWorldOfArtists,
        isSportsMuseumTemplate,
        isPhoenxholding,
        isSyndicate,
    } = useGetUniverseTemplate();
    const meta = useMeta('home', { projectName, projectUrl: domain! });

    const confValue = useMemo(() => ({ configurations }), [configurations]);

    const refetch = useCallback(() => {
        UniverseAPI.fetchUiConfiguration({
            namespace: 'main',
            enabled: true,
        }).then((r) => {
            setConfigurations(r.data.uiConfigurations ?? []);
        });
    }, []);

    const openVerificationModal = useCallback(() => {
        if (!isUniverse || visible || !isUniverseOwner) {
            return;
        }
        const item = window.localStorage.getItem(VERIFICATION_PROCESS_MODAL_ID);
        if (!item && isTechContemporary) {
            VerificationProcessTechModal.open({});
            return;
        }
        if (!item) {
            VerificationProcessModal.open({});
        }
    }, [isUniverse, visible, isUniverseOwner]);

    useEffect(() => {
        openVerificationModal();
    }, [openVerificationModal]);

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLayout = () => {
        if (isNinjaTemplate) {
            return <NinjaUniverseLanding />;
        }
        if (isOceanTemplate) {
            return <OceanUniverseLanding />;
        }
        if (isNftSuperlab) {
            return <SuperLabUniverseLanding />;
        }
        if (isTechContemporary) {
            return <TechContemporaryLanding />;
        }

        if (isWorldOfArtists) {
            return <WorldOfArtistsUniverseLanding />;
        }
        if (isSportsMuseumTemplate) {
            return <SportsMuseumUniverseLanding />;
        }
        if (isPhoenxholding) {
            return <PhoenxHoldingUniverseLanding />;
        }
        if (isSyndicate) {
            return <SyndicateUniverseLanding />;
        }

        return isUniverse ? <UniverseLanding /> : <S7Landing />;
    };

    return (
        <>
            <PageMeta title={meta.title} description={meta.description} />
            <UniverseConfigurationsContext.Provider key="home" value={confValue}>
                {getLayout()}
            </UniverseConfigurationsContext.Provider>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(
    (store) =>
        async ({ res, req, locale }) => {
            const { data } = await makeSSRApiRequest(
                {
                    req,
                    res,
                },
                async () =>
                    UniverseAPI.fetchUiConfiguration({
                        enabled: true,
                        namespace: 'main',
                    })
            );
            // @ts-ignore
            await store.dispatch(fetchArtsThunk());

            return {
                props: {
                    uiConfigurations: data.uiConfigurations,
                    ...(await serverSideTranslations(locale!, PAGES_LOCALES)),
                },
            };
        }
);

export default MarketplacePage;